import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import { useMockApi } from '@/lib/useMock'

/** Mock mode only: the sequence behind `mintDin`'s stand-in DINs. */
let mockDinSequence = 0

export type WebhookType =
  | 'cases'
  | 'instrument_results'
  | 'event_patient_new'
  | 'event_instrument_result'
  | 'event_patient_encounter'
  | 'event_patient_demographics'
  | 'event_lis_result'
/**
 * `no_change` is a SUCCESS: the delivery was valid and fully applied, and
 * applying it changed nothing — a semantically identical encounter redelivery.
 * Distinct from `processed` so the log can show it, and from `failed` so it is
 * not read as an error.
 */
export type WebhookOutcome = 'received' | 'processed' | 'no_change' | 'invalid' | 'failed'

/** Which synthetic instrument payload the integration-service mock should embed. */
export type TriggerResultType = 'ella' | 'nc200' | 'epic'

/** Named fixture scenarios, grouped by the instrument they belong to (mock-only). */
export const TRIGGER_SCENARIOS: Record<TriggerResultType, { label: string; scenarios: string[] }> = {
  ella: {
    label: 'ELLA Simple Plex (cytokine)',
    scenarios: ['happy-path', 'cv-exceeded', 'uloq-exceeded', 'quarantine', 'not-found'],
  },
  nc200: {
    label: 'NucleoCounter NC-200 (cell count / viability)',
    scenarios: ['nc200-happy', 'nc200-low-viability', 'nc200-quarantine'],
  },
  epic: {
    label: 'EPIC EMR (order)',
    scenarios: [
      'epic-happy',
      'epic-unresolvable',
      'epic-filing-filed',
      'epic-filing-pending',
      'epic-filing-rejected',
    ],
  },
}

export type SubscriptionStatus = 'active' | 'missing'

export type WebhookSubscription = {
  id: string
  tenantId: string
  channel: string
  hookId: string
  url: string
  /** The receiver URL the backend would register today; null when its receiver base is not configured. */
  expectedUrl: string | null
  /** The URL the integration-service actually holds for this hook; null when the hook is missing. */
  registeredUrl: string | null
  events: string[]
  connectionIntegrationId: string | null
  createdAt: string
  updatedAt: string
  status: SubscriptionStatus
}

/** The mock's per-subscriber delivery report, echoed back from a trigger. */
export type MockDelivery = { hookId: string; url: string; delivered: boolean; statusCode: number | null; ackCode?: string | null; error: string | null }
export type TriggerResult = { event?: unknown; deliveries?: MockDelivery[] } & Record<string, unknown>


/**
 * ADT trigger events the encounter trigger offers, with what each one does to a
 * visit. The receiver treats all five as one keyed upsert, so the only
 * structural difference is that A03 closes the visit.
 */
export const TRIGGER_ADT_EVENTS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'A01', label: 'A01 — admit' },
  { value: 'A04', label: 'A04 — register' },
  { value: 'A08', label: 'A08 — update' },
  { value: 'A02', label: 'A02 — transfer (moves the unit)' },
  { value: 'A03', label: 'A03 — discharge (closes the visit)' },
]

export type WebhookDelivery = {
  id: string
  tenantId: string
  type: WebhookType
  endpoint: string
  outcome: WebhookOutcome
  error: string | null
  serviceIdentity: string | null
  din: string | null
  productCode: string | null
  resultCount: number | null
  receivedAt: string
  payload?: unknown
}

/** Result of a simulated send — never throws on non-2xx so the UI can show 400s. */
export type SimulateResult = { ok: boolean; status: number; body: unknown }

async function simulate(path: string, rawJson: string): Promise<SimulateResult> {
  let parsed: unknown = null
  try {
    const res = await authFetch(`${BASE}${path}`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: rawJson,
    })
    const text = await res.text()
    try {
      parsed = text ? JSON.parse(text) : null
    } catch {
      parsed = text
    }
    return { ok: res.ok, status: res.status, body: parsed }
  } catch (e) {
    return { ok: false, status: 0, body: e instanceof Error ? e.message : 'Network error' }
  }
}

/** Session-authed JSON request against the backend (subscriptions, mint, triggers). */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders(init?.body ? { 'Content-Type': 'application/json' } : undefined),
    ...init,
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

export const webhooksApi = {
  /** POST a raw JSON string to the instrument-results receiver (the emitter's job in prod). */
  simulateInstrumentResults: (tenantId: string, rawJson: string) =>
    simulate(`/integrations/webhooks/${tenantId}/instrument-results`, rawJson),

  /** POST a raw JSON string to the cases receiver. */
  simulateCases: (tenantId: string, rawJson: string) =>
    simulate(`/integrations/webhooks/${tenantId}/cases`, rawJson),

  // ── Subscriptions (register the backend on the integration-service push channel) ──
  listSubscriptions: (tenantId: string) =>
    request<WebhookSubscription[]>(`/integrations/webhooks/${tenantId}/subscriptions`),

  createSubscription: (tenantId: string, body: { channel?: string; events?: string[] }) =>
    request<WebhookSubscription>(`/integrations/webhooks/${tenantId}/subscriptions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  removeSubscription: (tenantId: string, subId: string) =>
    request<{ removed: boolean }>(`/integrations/webhooks/${tenantId}/subscriptions/${subId}`, {
      method: 'DELETE',
    }),

  resubscribe: (tenantId: string, subId: string) =>
    request<WebhookSubscription>(
      `/integrations/webhooks/${tenantId}/subscriptions/${subId}/resubscribe`,
      { method: 'POST' },
    ),

  // ── DIN mint + mock-driven event triggers ────────────────────────────────────────
  mintDin: (
    tenantId: string,
    body: { patientMrn: string; procedureId?: string; formDefinitionId?: string; step?: string; productCode?: string },
  ) => {
    if (useMockApi) {
      // An ISBT-shaped stand-in for the mock harness: facility A9999, this year.
      const year = String(new Date().getUTCFullYear() % 100).padStart(2, '0')
      return Promise.resolve({ din: `A9999${year}${String(++mockDinSequence).padStart(6, '0')}` })
    }
    return request<{ din: string }>(`/integrations/webhooks/${tenantId}/din`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  triggerPatientNew: (
    tenantId: string,
    body: { patientId: string; demographics?: Record<string, unknown>; treatment?: Record<string, unknown> },
  ) =>
    request<TriggerResult>(`/integrations/webhooks/${tenantId}/trigger/patient-new`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Fire `patient.encounter.upsert` for one patient. The backend composes a
   * MedStar-shaped ADT so repeat triggers address the SAME visit; it echoes back
   * the `encounterNumber` it used.
   */
  triggerPatientEncounter: (
    tenantId: string,
    body: { patientId: string; trigger?: string; encounterNumber?: string },
  ) =>
    request<TriggerResult & { encounterNumber?: string }>(
      `/integrations/webhooks/${tenantId}/trigger/patient-encounter`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Fire `patient.demographics.upsert` (an Epic ADT A08) for one patient. It
   * updates the patient's details and never opens a procedure.
   */
  triggerPatientDemographics: (
    tenantId: string,
    body: { patientId: string; demographics?: Record<string, unknown> },
  ) =>
    request<TriggerResult>(`/integrations/webhooks/${tenantId}/trigger/patient-demographics`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  triggerInstrumentResult: (
    tenantId: string,
    body: {
      instrumentId?: string
      din: string
      resultType?: TriggerResultType
      scenario?: string
      variables?: Array<{ name: string; value: unknown }>
    },
  ) =>
    request<TriggerResult>(`/integrations/webhooks/${tenantId}/trigger/instrument-result`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Deliver ANY JSON to this tenant's event receiver, unchanged. It runs the
   * exact code a real delivery does, and is recorded as sent by the signed-in
   * user. Resolves to whatever the receiver answered.
   */
  triggerRawEvent: (tenantId: string, body: unknown) =>
    request<unknown>(`/integrations/webhooks/${tenantId}/trigger/raw-event`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Session-authed delivery log for a tenant. */
  listDeliveries: async (tenantId: string): Promise<WebhookDelivery[]> => {
    const res = await authFetch(`${BASE}/integrations/webhooks/${tenantId}/deliveries`, {
      headers: authHeaders(),
    })
    if (!res.ok) {
      handleUnauthorized(res.status)
      throw new Error(`Failed to load deliveries (${res.status})`)
    }
    return res.json() as Promise<WebhookDelivery[]>
  },
}
